// Loading screen: the map's levelshot, its name, a progress bar over the
// loadMap stages and a tip (the map's rules or a controls hint). When the map
// is ready, a click or Enter starts playing (the mouse capture needs a user
// gesture anyway). Maps with spawns for both teams ask for Axis or Allies.

import { bindLabel } from '../../game/binds';
import { Button } from '../components/Button';
import { ColorText } from '../components/ColorText';
import { Levelshot } from '../components/Levelshot';
import { useApp } from '../context';
import { describeRules, peekMapMeta } from '../maps/mapMeta';
import { TEAM_NAMES } from '../../shared/game/mapLogic';
import { game, loading, net, settings } from '../state';

function tips(): string[] {
  const b = settings.peek().binds;
  return [
    `Press ${bindLabel(b, 'save')} to save your position and ${bindLabel(b, 'load')} to go back to it (loading stops the run timer).`,
    `${bindLabel(b, 'respawn')} respawns you at the start and restarts the run.`,
    'Stamina is infinite, like on ETJump servers: sprint as much as you like.',
    'Strafe helper: keep the crosshair in the bright part of the green band to gain the most speed.',
    `Watch your last finished run with ${bindLabel(b, 'replay')}.`,
    'Physics run at 125 fps by default, like pmove_msec 8.',
    `Hold ${bindLabel(b, 'scoreboard')} to see who is in the room and their teams.`,
  ];
}

/**
 * Joining the map: Axis or Allies when both teams have spawns (the team picked
 * last has the focus), otherwise Start for the only team that can spawn.
 */
function TeamChoice() {
  const app = useApp();
  const teams = game.value?.teams ?? [0, 1];
  if (teams.length < 2) {
    return (
      <div class="team-choice">
        <Button variant="primary" size="lg" icon="play" hint="ENTER" autoFocus onClick={() => app.play()}>
          Start
        </Button>
        <span class="muted small">Team: {TEAM_NAMES[teams[0]]} (the only spawn on this map)</span>
      </div>
    );
  }
  const last = settings.peek().team;
  return (
    <div class="team-choice" role="group" aria-label="Choose your team">
      <div class="eyebrow">Choose your team</div>
      <div class="team-buttons">
        {([0, 1] as const).map((t) => (
          <Button key={t} variant={t === last ? 'primary' : 'secondary'} size="lg" icon="flag" hint={t === last ? 'ENTER' : undefined} autoFocus={t === last} class={`team-btn team-${t ? 'allies' : 'axis'}`} onClick={() => app.play(t)}>
            {TEAM_NAMES[t]}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function LoadingScreen() {
  const app = useApp();
  const l = loading.value;
  if (!l) return null;
  const ready = l.progress >= 1;
  const info = net.value.maps.find((m) => m.name === l.map);
  const meta = peekMapMeta(app.vfs, l.map);
  const longname = info?.longname ?? app.arenas.get(l.map)?.longname ?? meta?.message;
  const rules = info ? describeRules(info.rules) : meta ? describeRules(meta.rules) : [];
  const all = tips();
  // stable per map, so the tip does not change while loading
  const tip = rules.length ? `This map: ${rules.join(' · ')}.` : all[[...l.map].reduce((a, c) => a + c.charCodeAt(0), 0) % all.length];
  return (
    <div class={`loading-screen layer${ready ? ' ready' : ''}`} onClick={ready ? (e) => !(e.target as Element).closest('.team-choice') && app.play() : undefined}>
      <Levelshot map={l.map} eager class="loading-bg" />
      <div class="loading-shade" />
      <div class="loading-content">
        <div class="eyebrow">{ready ? 'Ready' : 'Loading'}</div>
        <h1 class="loading-title">{longname ? <ColorText text={longname} /> : l.map}</h1>
        <div class="loading-sub mono">{l.map}</div>
        <div class="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(l.progress * 100)} aria-label="Loading progress">
          <div class={`progress-fill${ready ? '' : ' busy'}`} style={{ transform: `scaleX(${Math.max(0.02, l.progress)})` }} />
        </div>
        <div class="loading-stage">{ready ? 'Map loaded' : `${l.stage}…`}</div>
        <p class="loading-tip">
          <span class="eyebrow">Tip</span> {tip}
        </p>
        {ready && <TeamChoice />}
      </div>
    </div>
  );
}
